import { basicStrapiRequest } from 'strapi/client';
import {
  ComponentBuilder,
  ComponentBuilderFetchDataInputs,
} from 'strapi/componentBuilder';
import { PageWithDescriptionsProps } from 'strapi/page/types';
import { pageTypes, pageTypesOldStructure } from 'strapi/pageTypes';

import { CategoryList } from '../categoryList';
import { CategoryListGridItem, CategoryProps } from '../categoryList/types';
import { collectImageComponentParams } from '../shared/collector';

import { OtherPagesAutoFilledStrapiComponent } from './types';

export class OtherPagesAutoFilledComponentBuilder extends ComponentBuilder {
  public key = 'list.other-pages-auto-filled' as const;

  async fetchData(inputs: ComponentBuilderFetchDataInputs) {
    const fieldName = this.getFieldNameSymbol(inputs.componentData);

    const page = await inputs.fetchManager.addRequestOptionsAndWaitForResponse<{
      fieldName: OtherPagesAutoFilledStrapiComponent;
    }>({
      populate: {
        [fieldName]: true,
      },
    });
    const componentData = page[fieldName];
    if (!componentData) {
      return undefined;
    }

    const otherPages: CategoryListGridItem[] = [];

    if (
      inputs.pageKey === pageTypes.SOLUTIONS_CHILD_COLLECTION_TYPE ||
      inputs.pageKey === pageTypes.FEATURES_PARENT_COLLECTION_TYPE
    ) {
      const pagesInfo = await basicStrapiRequest<PageWithDescriptionsProps>({
        pageKey: pageTypes.FEATURES_CHILD_COLLECTION_TYPE,
        requestObject: {
          populate: {
            cover: true,
          },
          fields: ['path', 'slug', 'descriptiveName', 'pageDescription'],
        },
      });

      if (!('error' in pagesInfo)) {
        const otherPagesList: CategoryListGridItem[] = pagesInfo.map(
          (page) => ({
            title: page.descriptiveName,
            url: page.path,
            image: collectImageComponentParams(page.cover)!,
          })
        );
        otherPages.push(...otherPagesList);
      }
    }

    if (inputs.pageKey === pageTypes.SOLUTIONS_PARENT_COLLECTION_TYPE) {
      const pagesInfo = await basicStrapiRequest<PageWithDescriptionsProps>({
        pageKey: pageTypes.SOLUTIONS_CHILD_COLLECTION_TYPE,
        requestObject: {
          populate: {
            cover: true,
          },
        },
      });

      if (!('error' in pagesInfo)) {
        const otherPagesList: CategoryListGridItem[] = pagesInfo.map(
          (page) => ({
            title: page.descriptiveName,
            url: page.path,
            image: collectImageComponentParams(page.cover)!,
          })
        );
        otherPages.push(...otherPagesList);
      }
    }

    if (
      inputs.pageKey === pageTypes.AI_USE_CASE_GENERATOR_PAGE_COLLECTION_TYPE
    ) {
      const pageKeys = [
        pageTypesOldStructure.MOCKUP_PARENT_PAGE_COLLECTION_TYPE,
        pageTypesOldStructure.AI_WRITING_PARENT_PAGE_COLLECTION_TYPE,
        pageTypes.AI_IMAGE_GENERATOR_COLLECTION_TYPE,
        pageTypes.AI_VECTOR_GENERATOR_COLLECTION_TYPE,
        pageTypes.VECTORIZER_COLLECTION_TYPE,
      ];

      const otherPagesList = await Promise.all(
        pageKeys.map(async (pageKey) => {
          const pagesInfo = await basicStrapiRequest<PageWithDescriptionsProps>(
            {
              pageKey: pageKey,
              requestObject: {
                populate: {
                  cover: true,
                },
              },
            }
          );
          if (!('error' in pagesInfo)) {
            let singlePage;
            if (Array.isArray(pagesInfo)) {
              singlePage = pagesInfo.length > 0 ? pagesInfo[0] : null;
            } else {
              singlePage = pagesInfo;
            }

            if (!singlePage) {
              return null;
            }

            return {
              title: singlePage.descriptiveName,
              url: singlePage.path,
              image: collectImageComponentParams(singlePage.cover)!,
            };
          }
          return null;
        })
      );

      otherPages.push(...otherPagesList.filter((x) => !!x));
    }

    if (otherPages.length === 0) {
      return undefined;
    }

    return {
      name: componentData.title,
      items: otherPages,
    } as CategoryProps;
  }

  render(inputs: Awaited<ReturnType<(typeof this)['fetchData']>>) {
    if (inputs) {
      return <CategoryList categories={[inputs]} withNavigation={false} />;
    } else {
      return null;
    }
  }
}
